import { useState, useEffect, useRef } from 'react'
import { supabase, db } from '../../lib/supabase'

export default function Reclamos() {
  const [reclamos, setReclamos] = useState([])
  const [solicitudesARCO, setSolicitudesARCO] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterEstado, setFilterEstado] = useState('')
  const [filterTipo, setFilterTipo] = useState('') // 'libro' o 'arco'
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedReclamo, setSelectedReclamo] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [respuesta, setRespuesta] = useState('')
  const [saving, setSaving] = useState(false)
  const [firmaSignedUrl, setFirmaSignedUrl] = useState(null)
  const [arcoSignedUrls, setArcoSignedUrls] = useState({})
  
  // Estados para visibilidad de páginas
  const [libroVisible, setLibroVisible] = useState(true)
  const [arcoVisible, setArcoVisible] = useState(true)
  const [togglingPage, setTogglingPage] = useState(null)

  // Estado para dropdown de exportar
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    fetchReclamos()
    loadPageVisibility()
  }, [])

  // Cerrar dropdown al clickear fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadPageVisibility = async () => {
    try {
      const [libroResult, arcoResult] = await Promise.all([
        db.getPageVisibility('libro_reclamaciones'),
        db.getPageVisibility('derechos_arco')
      ])
      
      setLibroVisible(libroResult.visible)
      setArcoVisible(arcoResult.visible)
    } catch (error) {
      console.error('Error loading page visibility:', error)
    }
  }

  const fetchReclamos = async () => {
    try {
      setLoading(true)
      
      // Fetch libro de reclamaciones
      const { data: libroData, error: libroError } = await supabase
        .from('libro_reclamaciones')
        .select('*')
        .order('created_at', { ascending: false })

      if (libroError) throw libroError
      
      // Fetch solicitudes ARCO
      const { data: arcoData, error: arcoError } = await supabase
        .from('solicitudes_arco')
        .select('*')
        .order('created_at', { ascending: false })

      if (arcoError) throw arcoError
      
      setReclamos(libroData || [])
      setSolicitudesARCO(arcoData || [])
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateEstado = async (id, nuevoEstado, tipo) => {
    const table = tipo === 'arco' ? 'solicitudes_arco' : 'libro_reclamaciones'
    const { error } = await supabase
      .from(table)
      .update({ estado: nuevoEstado })
      .eq('id', id)

    if (!error) {
      fetchReclamos()
      if (selectedReclamo?.id === id) {
        setSelectedReclamo({ ...selectedReclamo, estado: nuevoEstado })
      }
    }
  }

  const saveRespuesta = async () => {
    if (!respuesta.trim()) {
      alert('Por favor, escribe una respuesta')
      return
    }

    setSaving(true)
    try {
      const table = selectedReclamo.tipo_fuente === 'arco' ? 'solicitudes_arco' : 'libro_reclamaciones'
      const { error } = await supabase
        .from(table)
        .update({ 
          respuesta: respuesta,
          estado: 'En proceso'
        })
        .eq('id', selectedReclamo.id)

      if (error) throw error

      alert('Respuesta guardada exitosamente')
      setRespuesta('')
      fetchReclamos()
      setShowModal(false)
    } catch (error) {
      console.error('Error:', error)
      alert('Error al guardar la respuesta')
    } finally {
      setSaving(false)
    }
  }

  const exportLibroReclamacionesCSV = () => {
    if (reclamos.length === 0) {
      alert('No hay datos de Libro de Reclamaciones para exportar')
      return
    }

    const headers = [
      'Folio',
      'Tipo Registro',
      'Nombres',
      'Apellido Paterno',
      'Apellido Materno',
      'DNI/CE',
      'Email',
      'Teléfono',
      'Domicilio',
      'Distrito',
      'Es Menor',
      'Tipo Bien',
      'Descripción Bien',
      'Monto Reclamado',
      'Moneda',
      'Problema Principal',
      'Detalle Problema',
      'Pedido',
      'Estado',
      'Respuesta',
      'Fecha Creación',
      'Última Actualización'
    ]

    const rows = reclamos.map(r => [
      r.folio || '',
      r.tipo_registro || '',
      r.nombres || '',
      r.apellido_paterno || '',
      r.apellido_materno || '',
      r.dni_ce || '',
      r.email || '',
      r.telefono || '',
      r.domicilio || '',
      r.distrito || '',
      r.es_menor_edad ? 'Sí' : 'No',
      r.tipo_bien || '',
      r.descripcion_bien || '',
      r.monto_reclamado || '',
      r.moneda_monto || '',
      r.problema_principal || '',
      r.detalle_problema || '',
      r.pedido || '',
      r.estado || '',
      r.respuesta || '',
      new Date(r.created_at).toLocaleString('es-PE'),
      new Date(r.updated_at).toLocaleString('es-PE')
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `libro_reclamaciones_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    setShowExportDropdown(false)
  }

  const exportDerechosARCOCSV = () => {
    if (solicitudesARCO.length === 0) {
      alert('No hay datos de Derechos ARCO para exportar')
      return
    }

    const headers = [
      'Folio',
      'Tipo Solicitud',
      'Titular Nombres',
      'Titular Apellidos',
      'Titular Documento',
      'Titular Email',
      'Titular Domicilio',
      'Es Representante',
      'Representante Nombres',
      'Representante Apellidos',
      'Representante Documento',
      'Detalle Solicitud',
      'Estado',
      'Respuesta',
      'Fecha Creación',
      'Última Actualización'
    ]

    const rows = solicitudesARCO.map(r => [
      r.folio || '',
      r.tipo_solicitud || '',
      r.titular_nombres || '',
      r.titular_apellidos || '',
      r.titular_documento || '',
      r.titular_email || '',
      r.titular_domicilio || '',
      r.es_representante ? 'Sí' : 'No',
      r.representante_nombres || '',
      r.representante_apellidos || '',
      r.representante_documento || '',
      r.detalle_solicitud || '',
      r.estado || '',
      r.respuesta || '',
      new Date(r.created_at).toLocaleString('es-PE'),
      new Date(r.updated_at).toLocaleString('es-PE')
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `derechos_arco_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    setShowExportDropdown(false)
  }

  const togglePageVisibility = async (pageName, currentState) => {
    const pageLabel = pageName === 'libro_reclamaciones' ? 'Libro de Reclamaciones' : 'Derechos ARCO'
    const newState = !currentState
    const action = newState ? 'mostrar' : 'ocultar'
    
    if (!confirm(`¿Estás seguro de ${action} la página "${pageLabel}" para el público?`)) {
      return
    }

    setTogglingPage(pageName)
    try {
      const { error } = await db.setPageVisibility(pageName, newState)
      
      if (error) throw error
      
      // Actualizar estado local
      if (pageName === 'libro_reclamaciones') {
        setLibroVisible(newState)
      } else {
        setArcoVisible(newState)
      }
      
      alert(`✅ Página "${pageLabel}" ${newState ? 'visible' : 'oculta'} exitosamente`)
    } catch (error) {
      console.error('Error toggling page visibility:', error)
      alert('❌ Error al cambiar la visibilidad de la página')
    } finally {
      setTogglingPage(null)
    }
  }

  // Combinar ambas fuentes de datos y agregar tipo_fuente
  const allRecords = [
    ...reclamos.map(r => ({ ...r, tipo_fuente: 'libro' })),
    ...solicitudesARCO.map(r => ({ ...r, tipo_fuente: 'arco' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  const filteredReclamos = allRecords.filter(reclamo => {
    const nombre = reclamo.tipo_fuente === 'arco' 
      ? `${reclamo.titular_nombres} ${reclamo.titular_apellidos}`
      : `${reclamo.nombres} ${reclamo.apellido_paterno}`
    const email = reclamo.tipo_fuente === 'arco' ? reclamo.titular_email : reclamo.email
    
    const matchesSearch = 
      nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reclamo.folio && reclamo.folio.toString().includes(searchTerm.toLowerCase()))
    
    const matchesEstado = !filterEstado || reclamo.estado === filterEstado
    const matchesTipo = !filterTipo || 
      (filterTipo === 'libro' && reclamo.tipo_fuente === 'libro') ||
      (filterTipo === 'arco' && reclamo.tipo_fuente === 'arco')

    return matchesSearch && matchesEstado && matchesTipo
  })

  const getEstadoBadge = (estado) => {
    const colors = {
      'Pendiente': 'bg-orange-100 text-orange-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      'Resuelto': 'bg-green-100 text-green-800',
      'Cerrado': 'bg-gray-100 text-gray-800'
    }
    return colors[estado] || 'bg-gray-100 text-gray-800'
  }

  const reclamosPendientes = allRecords.filter(r => r.estado === 'Pendiente').length

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Reclamos y Solicitudes</h1>
          <p className="text-gray-600 mt-1">
            {reclamosPendientes} solicitud{reclamosPendientes !== 1 ? 'es' : ''} pendiente{reclamosPendientes !== 1 ? 's' : ''}
          </p>
        </div>
        
        {/* Dropdown Exportar CSV */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowExportDropdown(!showExportDropdown)}
            disabled={reclamos.length === 0 && solicitudesARCO.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            📄 Exportar CSV
            <svg 
              className={`w-4 h-4 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showExportDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border z-10">
              <button
                onClick={exportLibroReclamacionesCSV}
                disabled={reclamos.length === 0}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 border-b"
              >
                <span className="text-xl">📖</span>
                <div>
                  <p className="font-medium text-sm">Libro de Reclamaciones</p>
                  <p className="text-xs text-gray-500">{reclamos.length} registros</p>
                </div>
              </button>
              
              <button
                onClick={exportDerechosARCOCSV}
                disabled={solicitudesARCO.length === 0}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
              >
                <span className="text-xl">🔒</span>
                <div>
                  <p className="font-medium text-sm">Derechos ARCO</p>
                  <p className="text-xs text-gray-500">{solicitudesARCO.length} registros</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Configuración de Visibilidad de Páginas Públicas */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-lg font-bold mb-4">📝 Configuración de Páginas Públicas</h2>
        <p className="text-sm text-gray-600 mb-4">
          Controla si las páginas públicas están visibles para todos los usuarios. El módulo interno y los datos siempre permanecerán accesibles.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Toggle Libro de Reclamaciones */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📖</span>
              <div>
                <p className="font-semibold">Libro de Reclamaciones</p>
                <p className="text-xs text-gray-500">Página pública de formulario</p>
              </div>
            </div>
            <button
              onClick={() => togglePageVisibility('libro_reclamaciones', libroVisible)}
              disabled={togglingPage === 'libro_reclamaciones'}
              className={`relative inline-flex items-center justify-center h-8 w-16 rounded-md transition-all duration-200 ${
                libroVisible ? 'bg-green-600' : 'bg-gray-400'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span className="text-white text-xs font-bold tracking-wider">
                {libroVisible ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>

          {/* Toggle Derechos ARCO */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <p className="font-semibold">Derechos ARCO</p>
                <p className="text-xs text-gray-500">Página pública de solicitudes</p>
              </div>
            </div>
            <button
              onClick={() => togglePageVisibility('derechos_arco', arcoVisible)}
              disabled={togglingPage === 'derechos_arco'}
              className={`relative inline-flex items-center justify-center h-8 w-16 rounded-md transition-all duration-200 ${
                arcoVisible ? 'bg-green-600' : 'bg-gray-400'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span className="text-white text-xs font-bold tracking-wider">
                {arcoVisible ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            ℹ️ <strong>Nota:</strong> Al ocultar una página, esta no estará disponible para el público y se ocultará del footer. Los datos históricos y el acceso interno no se verán afectados.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Buscar por nombre, email o folio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          />
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Todos los tipos</option>
            <option value="libro">📖 Libro de Reclamaciones</option>
            <option value="arco">🔒 Derechos ARCO</option>
          </select>
          <select
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Todos los estados</option>
            <option value="Pendiente">Pendiente</option>
            <option value="En proceso">En proceso</option>
            <option value="Resuelto">Resuelto</option>
            <option value="Cerrado">Cerrado</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-2xl font-bold text-primary">{allRecords.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Pendientes</p>
          <p className="text-2xl font-bold text-orange-600">{reclamosPendientes}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">En Proceso</p>
          <p className="text-2xl font-bold text-blue-600">
            {allRecords.filter(r => r.estado === 'En proceso').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Resueltos</p>
          <p className="text-2xl font-bold text-green-600">
            {allRecords.filter(r => r.estado === 'Resuelto').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Cerrados</p>
          <p className="text-2xl font-bold text-gray-600">
            {allRecords.filter(r => r.estado === 'Cerrado').length}
          </p>
        </div>
      </div>

      {/* Tabla de Reclamos */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">Cargando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reclamante</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReclamos.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      No se encontraron reclamos
                    </td>
                  </tr>
                ) : (
                  filteredReclamos.map((reclamo) => (
                    <tr key={reclamo.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm font-semibold text-primary">
                          {reclamo.folio}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">
                            {reclamo.tipo_fuente === 'arco' 
                              ? `${reclamo.titular_nombres} ${reclamo.titular_apellidos}`
                              : `${reclamo.nombres} ${reclamo.apellido_paterno}`
                            }
                          </p>
                          <p className="text-sm text-gray-500">
                            {reclamo.tipo_fuente === 'arco' ? reclamo.titular_email : reclamo.email}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {reclamo.tipo_fuente === 'arco' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            🔒 {reclamo.tipo_solicitud}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            📖 {reclamo.tipo_registro}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoBadge(reclamo.estado)}`}>
                          {reclamo.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {new Date(reclamo.created_at).toLocaleDateString('es-PE')}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={async () => {
                            setSelectedReclamo(reclamo)
                            setRespuesta(reclamo.respuesta || '')
                            setShowModal(true)

                            // Generate signed URLs for private files
                            setFirmaSignedUrl(null)
                            setArcoSignedUrls({})

                            if (reclamo.tipo_fuente === 'libro' && reclamo.firma_url) {
                              // Signature from reclamaciones bucket
                              const { data, error } = await supabase
                                .storage
                                .from('reclamaciones')
                                .createSignedUrl(reclamo.firma_url, 3600)

                              if (!error && data?.signedUrl) {
                                setFirmaSignedUrl(data.signedUrl)
                              }
                            } else if (reclamo.tipo_fuente === 'arco') {
                              // ARCO attachments from arco bucket
                              const urls = {}
                              const fields = ['titular_dni_url', 'representante_dni_url', 'representante_acreditacion_url', 'documentos_adicionales_url']
                              
                              for (const field of fields) {
                                if (reclamo[field]) {
                                  const { data, error } = await supabase
                                    .storage
                                    .from('arco')
                                    .createSignedUrl(reclamo[field], 3600)

                                  if (!error && data?.signedUrl) {
                                    urls[field] = data.signedUrl
                                  }
                                }
                              }
                              setArcoSignedUrls(urls)
                            }
                          }}
                          className="text-primary hover:text-blue-800 text-sm font-medium"
                        >
                          Ver Detalle →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalle */}
      {showModal && selectedReclamo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full my-8" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '896px', maxHeight: '90vh' }}>
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">
                  {selectedReclamo.tipo_fuente === 'arco' ? 'Solicitud ARCO' : 'Reclamo'} - {selectedReclamo.folio}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Registrado el {new Date(selectedReclamo.created_at).toLocaleDateString('es-PE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                type="button"
              >
                ×
              </button>
            </div>
            <div className="p-6 border-b">
              <div className="flex gap-2">
                  {['Pendiente', 'En proceso', 'Resuelto', 'Cerrado'].map(estado => (
                    <button
                      key={estado}
                      onClick={() => updateEstado(selectedReclamo.id, estado, selectedReclamo.tipo_fuente)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        selectedReclamo.estado === estado
                          ? getEstadoBadge(estado)
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {estado}
                    </button>
                  ))}
                </div>
              </div>
            
            <div className="p-6 max-h-[calc(90vh-250px)] overflow-y-auto">
              {selectedReclamo.tipo_fuente === 'arco' ? (
                /* ARCO Modal Content */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Titular Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-3">👤 Titular</h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Nombre:</span> {selectedReclamo.titular_nombres} {selectedReclamo.titular_apellidos}</p>
                      <p><span className="font-medium">Email:</span> {selectedReclamo.titular_email}</p>
                      <p><span className="font-medium">Documento:</span> {selectedReclamo.titular_documento}</p>
                      <p><span className="font-medium">Domicilio:</span> {selectedReclamo.titular_domicilio}</p>
                      {selectedReclamo.titular_dni_url && (
                        <p><a href={arcoSignedUrls.titular_dni_url || '#'} target="blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">🗄️ Ver DNI</a></p>
                      )}
                    </div>
                  </div>

                  {/* Representante */}
                  {selectedReclamo.es_representante && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-bold mb-3">🧑‍⚖️ Representante Legal</h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Nombre:</span> {selectedReclamo.representante_nombres} {selectedReclamo.representante_apellidos}</p>
                        <p><span className="font-medium">Documento:</span> {selectedReclamo.representante_documento}</p>
                        {selectedReclamo.representante_dni_url && (
                          <p><a href={arcoSignedUrls.representante_dni_url || '#'} target="blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">🗄️ Ver DNI</a></p>
                        )}
                        {selectedReclamo.representante_acreditacion_url && (
                          <p><a href={arcoSignedUrls.representante_acreditacion_url || '#'} target="blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">📄 Ver Acreditación</a></p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tipo de Solicitud */}
                  <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-3">🔒 Tipo de Solicitud ARCO</h3>
                    <div className="space-y-3 text-sm">
                      <p><span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 rounded-full font-medium">{selectedReclamo.tipo_solicitud}</span></p>
                      <div>
                        <p className="font-medium mb-1">Detalle de la Solicitud:</p>
                        <p className="bg-white p-3 rounded border whitespace-pre-line">{selectedReclamo.detalle_solicitud}</p>
                      </div>
                      {selectedReclamo.documentos_adicionales_url && (
                        <p><a href={arcoSignedUrls.documentos_adicionales_url || '#'} target="blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">📄 Ver Documentos Adicionales</a></p>
                      )}
                    </div>
                  </div>

                  {/* Respuesta */}
                  <div className="md:col-span-2">
                    <h3 className="font-bold mb-3">💬 Respuesta a la Solicitud</h3>
                    <textarea
                      value={respuesta}
                      onChange={(e) => setRespuesta(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Escribe la respuesta a la solicitud ARCO..."
                    />
                    <button
                      onClick={saveRespuesta}
                      disabled={saving || !respuesta.trim()}
                      className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? 'Guardando...' : 'Guardar Respuesta'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Libro de Reclamaciones Modal Content */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Información Personal */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold mb-3">👤 Información Personal</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Nombre:</span> {selectedReclamo.nombres} {selectedReclamo.apellido_paterno} {selectedReclamo.apellido_materno}</p>
                    <p><span className="font-medium">DNI/CE:</span> {selectedReclamo.dni_ce}</p>
                    <p><span className="font-medium">Email:</span> {selectedReclamo.email}</p>
                    {selectedReclamo.email_confirmacion && selectedReclamo.email_confirmacion !== selectedReclamo.email && (
                      <p><span className="font-medium">Email Confirmación:</span> {selectedReclamo.email_confirmacion}</p>
                    )}
                    <p><span className="font-medium">Teléfono:</span> {selectedReclamo.telefono}</p>
                    <p><span className="font-medium">Domicilio:</span> {selectedReclamo.domicilio}, {selectedReclamo.distrito}</p>
                    {selectedReclamo.es_menor_edad && <p className="text-orange-600 font-medium">⚠️ Menor de edad</p>}
                    {selectedReclamo.es_vecino_centro_comercial && <p className="text-blue-600 font-medium">🏢 Vecino del Centro Comercial</p>}
                  </div>
                </div>

                {/* Información del Bien */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold mb-3">📦 Bien Contratado</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Tipo:</span> {selectedReclamo.tipo_bien}</p>
                    <p><span className="font-medium">Descripción:</span> {selectedReclamo.descripcion_bien}</p>
                    {selectedReclamo.monto_reclamado && (
                      <p><span className="font-medium">Monto:</span> {selectedReclamo.moneda_monto} {selectedReclamo.monto_reclamado}</p>
                    )}
                  </div>
                </div>

                {/* Detalles del Reclamo */}
                <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold mb-3">📝 Detalles del {selectedReclamo.tipo_registro}</h3>
                  <div className="space-y-3 text-sm">
                    {selectedReclamo.problema_principal && (
                      <p><span className="font-medium">Problema Principal:</span> {selectedReclamo.problema_principal}</p>
                    )}
                    <div>
                      <p className="font-medium mb-1">Detalle:</p>
                      <p className="bg-white p-3 rounded border whitespace-pre-line">{selectedReclamo.detalle_problema}</p>
                    </div>
                    <div>
                      <p className="font-medium mb-1">Pedido:</p>
                      <p className="bg-white p-3 rounded border whitespace-pre-line">{selectedReclamo.pedido}</p>
                    </div>
                  </div>
                </div>

                {/* Firma */}
                {(selectedReclamo.firma_digital || selectedReclamo.firma_url) && (
                  <div className="md:col-span-2">
                    <h3 className="font-bold mb-3">✍️ Firma Digital</h3>
                    <div className="border rounded-lg p-4 bg-white">
                      {selectedReclamo.firma_digital && (
                        <img src={firmaSignedUrl || selectedReclamo.firma_digital} alt="Firma" className="max-w-md mb-3" />
                      )}
                      {selectedReclamo.firma_url && firmaSignedUrl && (
                        <a 
                          href={firmaSignedUrl} 
                          download="firma.png"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          📥 Descargar Firma
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Respuesta */}
                <div className="md:col-span-2">
                  <h3 className="font-bold mb-3">💬 Respuesta al Reclamo</h3>
                  <textarea
                    value={respuesta}
                    onChange={(e) => setRespuesta(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Escribe la respuesta al reclamo..."
                  />
                  <button
                    onClick={saveRespuesta}
                    disabled={saving || !respuesta.trim()}
                    className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Guardando...' : 'Guardar Respuesta'}
                  </button>
                </div>
              </div>
              )}
            </div>

            <div className="p-6 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
